import { Component, createRef } from "react"
import PropTypes from "prop-types"

import classnames from "classnames"
import Fittty from "shared/components/fittty"
import colors from "foundation/_colors_export.scss?export"
import SuccessCheckmark from "./success_checkmark"
import LoaderDots from "shared/components/loader_dots"
import { expireBFCache } from "shared/scripts/back_forward_cache_utils"

// Connect/disconnect animate via CSS transitions (the Lottie success animation
// and progress meter were retired). Disconnect spans two components: clicking a
// connected button shows the pending feedback here, then ReconnectButton runs
// the knob slide once the status flips.
const CONNECTING_HOLD = 2000 // ms held on "Connecting…" (one loop of the loader-dots SVG)
const SUCCESS_HOLD = 1000 // ms the checkmark stays up before "Connected"

export default class ConnectButton extends Component {
  // off → connecting → success → on (connect); on → disconnecting (hands off)
  state = {
    phase: "off",
    ctaCopy: this.props.ctaCopy,
    sliding: false, // true while the knob runs its L→R slide (skipped if GrantAccess already did it)
  }

  buttonRef = createRef()

  componentDidMount() {
    if (this.props.connected && this.props.connectClicked) {
      this.animateConnect()
    } else {
      // Already connected on load (no click this session): settle in place.
      this.setState({ phase: "on", ctaCopy: "Connected" })
    }
  }

  componentWillUnmount() {
    clearTimeout(this.connectingTimer)
    clearTimeout(this.successTimer)
  }

  animateConnect = () => {
    const ctaCopy = `Connecting ${this.props.mainService.short_name}`
    if (this.props.animation?.connectSlide?.played) {
      // GrantAccess already ran the knob slide — start with the knob right.
      this.setState({ phase: "connecting", ctaCopy, sliding: false })
    } else {
      // First connecting component: flush the "off" baseline so the slide +
      // crossfades animate, and mark it played for the next component.
      if (this.buttonRef.current) void this.buttonRef.current.offsetWidth
      this.setState({ phase: "connecting", ctaCopy, sliding: true })
      this.props.updateAnimationCol?.({ animation: "connectSlide", status: { played: true } })
    }
    this.connectingTimer = setTimeout(this.enterSuccess, CONNECTING_HOLD)
  }

  enterSuccess = () => {
    this.setState({ phase: "success", ctaCopy: "" })
    this.successTimer = setTimeout(this.enterConnected, SUCCESS_HOLD)
  }

  enterConnected = () => {
    this.setState({ phase: "on", ctaCopy: "Connected" })
  }

  handleClick = event => {
    event.preventDefault()
    expireBFCache()
    // Pending disconnect feedback; the knob slides left once the request confirms.
    this.setState({ phase: "disconnecting", ctaCopy: "Disconnecting" })
    this.props.onConnectClick()
  }

  render() {
    const { phase, ctaCopy, sliding } = this.state
    const { knobBgColorForDarkBrands, dynamicConfig, mainService } = this.props

    const knobRight = phase !== "off"
    const trackIsOn = phase === "connecting" || phase === "success" || phase === "on"
    // Only gate transitions when actually sliding (skipped when GrantAccess already
    // slid, so the knob starts right without snapping) or during the disconnect crossfade.
    const isAnimating = (phase === "connecting" && sliding) || phase === "disconnecting"
    const hasIcon = dynamicConfig
    // White only while the knob sits right without an icon; recolours as it slides.
    const knobColor = knobRight && !hasIcon ? "#fff" : `#${mainService.brand_color}`
    // With an icon (dynamic applet) the track stays #111 and only the knob carries
    // the service colour. Without an icon the track takes the service colour (on)
    // / DSL "Loading" grey (disconnecting) / #111 (off), and the knob turns white.
    const trackColor = hasIcon
      ? colors.primaryTextColor
      : phase === "disconnecting"
        ? colors.connectButtonLoadingColor
        : trackIsOn
          ? `#${mainService.brand_color}`
          : colors.primaryTextColor

    return (
      <div className="connect-button-outline ca">
        <div
          ref={this.buttonRef}
          style={{ backgroundColor: trackColor }}
          className={classnames("connect-button", { "is-animating": isAnimating })}
          onClick={this.handleClick}
        >
          <div
            id="connect-ball"
            className={classnames("service-icon", {
              "alt-brand-color": knobBgColorForDarkBrands,
              connected: knobRight,
            })}
            style={{ backgroundColor: knobColor }}
          >
            {hasIcon && (
              <img
                src={mainService.lrg_monochrome_image_url || mainService.lrg_variant_image_url}
                width="40"
                height="40"
                alt={mainService.name}
                title={mainService.name}
              />
            )}
          </div>
          <div
            className={classnames("connect-cta", {
              connected: knobRight,
              "is-hidden": phase === "success",
            })}
          >
            <div>
              <span className="cta-label">
                <Fittty maxSize={this.props.connectButtonMaxFontSize} minSize={14} text={ctaCopy} />
              </span>
              {(phase === "connecting" || phase === "disconnecting") && (
                <LoaderDots className="connecting-dots" />
              )}
            </div>
          </div>
          <div className={classnames("connect-checkmark", { "is-visible": phase === "success" })} aria-hidden="true">
            <SuccessCheckmark />
          </div>
        </div>
      </div>
    )
  }
}

ConnectButton.propTypes = {
  connectClicked: PropTypes.bool.isRequired,
  mainService: PropTypes.object.isRequired,
  onConnectClick: PropTypes.func.isRequired,
  ctaCopy: PropTypes.string,
  connected: PropTypes.bool,
  dynamicConfig: PropTypes.bool,
  knobBgColorForDarkBrands: PropTypes.bool,
  connectButtonMaxFontSize: PropTypes.number,
  animation: PropTypes.object,
  updateAnimationCol: PropTypes.func,
}

ConnectButton.defaultProps = {
  ctaCopy: "Connect",
  connected: false,
  connectButtonMaxFontSize: 34,
}
